import { gql, useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '@/admins/SmartReports/types';
import type { Asset } from '@/types/GraphqlSchema';
import { ComponentTable } from '../Common/ComponentTable';
import { ComponentTableBody } from '../Common/ComponentTableBody';
import { ComponentTableHeader } from '../Common/ComponentTableHeader';
import { LoadingErrorWrapper } from '../Common/LoadingErrorWrapper';
import type {
  ComponentDefinition,
  ComponentEditProps,
  ComponentViewProps,
  UseDataType,
} from '../types';

export const COMPONENT_ID = 'asset_well_data_table';

export const ASSET_WELL_DATA_QUERY = gql`
  query assetWellData($input: EntityIdentifierInput!) {
    assetV2(input: $input) {
      id
      country {
        displayName
        name
      }
      formattedHydrocarbonType
      formattedNpv
      formattedRemainingCapex
      formattedNpvBoe
      formattedRemainingReserves
      breakevenOil
      currentDate
      estimatedCurrentDate
      formattedRecentProdLiquidsBbld
      formattedRecentProdGasMmcfd
      reserve {
        formattedTotalRemainingReservesPercentage
      }
      currentParticipations {
        id
        isCompanyOperator
        formattedPercentStake
        company {
          id
          name
        }
      }
    }
  }
`;

const formatMonthYear = (date?: string | null): string | null => {
  if (!date) return null;

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;

  return parsed
    .toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    })
    .replace(' ', '-');
};

const buildKeyDataRows = (asset: Asset | null | undefined) => {
  const reservesDate =
    formatMonthYear(asset?.currentDate) ?? formatMonthYear(asset?.estimatedCurrentDate) ?? 'N/A';

  const rows = [
    { label: 'Country', value: asset?.country?.displayName ?? asset?.country?.name ?? 'N/A' },
    { label: 'Primary Hydrocarbon', value: asset?.formattedHydrocarbonType ?? 'N/A' },
    { label: 'NPV10 (US$MM)', value: asset?.formattedNpv ?? 'N/A' },
    { label: 'Remaining Capex + Abex (US$MM)', value: asset?.formattedRemainingCapex ?? 'N/A' },
    { label: 'NPV10 per Barrel of Reserves (US$/boe)', value: asset?.formattedNpvBoe ?? 'N/A' },
    { label: 'Remaining Reserves (MMboe)', value: asset?.formattedRemainingReserves ?? 'N/A' },
    {
      label: `% Reserves Remaining (${reservesDate})`,
      value: asset?.reserve?.formattedTotalRemainingReservesPercentage ?? 'N/A',
    },
    {
      label: `Liquids Production (${reservesDate}) (bbl/d)`,
      value: asset?.formattedRecentProdLiquidsBbld ?? 'N/A',
    },
    {
      label: `Gas Production (${reservesDate}) (MMcf/d)`,
      value: asset?.formattedRecentProdGasMmcfd ?? 'N/A',
    },
    { label: 'Brent Oil Price Breakeven (US$/bbl)', value: asset?.breakevenOil ?? 'N/A' },
  ];

  return rows;
};

const buildParticipantRows = (asset: Asset | null | undefined) => {
  if (!asset?.currentParticipations?.length) {
    return [{ label: 'No participants available', value: '' }];
  }

  return asset.currentParticipations
    .filter(participation => participation?.company?.name)
    .map(participation => {
      const operatorSuffix = participation?.isCompanyOperator ? ' (Operator)' : '';
      return {
        label: `${participation?.company?.name}${operatorSuffix}`,
        value: participation?.formattedPercentStake ?? '',
      };
    });
};

const useData = ({ portfolio, persistedData, onComponentLoad }: UseDataType<Asset>) => {
  const [dataFetched, setDataFetched] = useState<Asset | null | undefined>(persistedData);
  const assetId = portfolio?.assets?.[0];
  const shouldSkipQuery = !!persistedData || !assetId;

  const {
    data: queryData,
    loading,
    error,
  } = useQuery<{ assetV2: Asset }>(ASSET_WELL_DATA_QUERY, {
    variables: { input: { id: assetId } },
    skip: shouldSkipQuery,
  });

  useEffect(() => {
    if (shouldSkipQuery || !queryData?.assetV2) return;

    const data = queryData.assetV2;
    setDataFetched(data);
    onComponentLoad?.({
      componentId: COMPONENT_ID,
      data,
    });
  }, [shouldSkipQuery, queryData, onComponentLoad]);

  return { data: dataFetched ?? ({} as Asset), loading, error };
};

const ComponentView = ({ data }: ComponentViewProps<Asset>) => {
  const keyDataRows = useMemo(() => buildKeyDataRows(data), [data]);
  const participantRows = useMemo(() => buildParticipantRows(data), [data]);

  return (
    <Box
      display='flex'
      flexDirection='column'
      width='100%'
      height='100%'
      maxWidth='100%'
      maxHeight='100%'
      overflow='auto'
      gap='1rem'
      data-testid='asset-well-data-table'
    >
      <ComponentTable sx={{ flex: 'initial', minHeight: '4.2rem' }}>
        <ComponentTableHeader>Key Data</ComponentTableHeader>
        <ComponentTableBody rows={keyDataRows} />
      </ComponentTable>

      <ComponentTable sx={{ flex: 'initial', minHeight: '4.2rem' }}>
        <ComponentTableHeader>Participants</ComponentTableHeader>
        <ComponentTableBody rows={participantRows} />
      </ComponentTable>
    </Box>
  );
};

const ComponentEdit = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: ComponentEditProps<Asset>) => {
  const { data, loading, error } = useData({
    portfolio,
    persistedData,
    onComponentLoad,
  });

  return (
    <LoadingErrorWrapper loading={loading} error={error} data={data}>
      <ComponentView data={data} />
    </LoadingErrorWrapper>
  );
};

export const AssetWellDataTableComponent: ComponentDefinition<Asset> = {
  id: COMPONENT_ID,
  name: 'Asset Well Data',
  type: SmartReportComponentTypesEnum.Table,
  reportTypes: [SmartReportTypeEnum.Asset],
  Component: ComponentEdit,
  ComponentView,
};
